import { OnDestroy } from '@angular/core';
import { map, finalize, catchError, mergeMap } from 'rxjs/operators';
import { AuthHTTPService } from './auth-http/auth-http.service';
import { AuthModel, SsoResponseModel } from './../_models/auth.model';
import { environment } from './../../../../environments/environment';
import { UserModel } from './../_models/user.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subscription, BehaviorSubject, of } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { getHeaderToken } from 'src/app/core/helpers/http-utils';
import { SSOConfigModel } from 'src/app/core/_models/sso/sso.config.model';
import { UserInfo } from 'src/app/modules/home/_models/user-info.model';
import { userInfo } from 'os';
import { Base64 } from '../../commons/ioc-v2-common/_base64';
import { StatusCodeData, checkCodeApi } from '../../commons/ultis/status.code';
import { MESSAGE_CONST } from 'src/app/_metronic/core/const/message.const';
import { ConfigurationModel } from '../../_models/configuration/configuration.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private subscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private nkgAuthToken = `${environment.appVersion}-${environment.NKGAUTH_KEY}`;
  private iocAccessToken = `${environment.appVersion}-${environment.IOC_ACCESS_TOKEN_KEY}`;
  private iocRefreshToken = `${environment.appVersion}-${environment.IOC_REFRESH_TOKEN_KEY}`;
  private iocExpiresToken = `${environment.appVersion}-${environment.IOC_EXPIRES_TOKEN_KEY}`;
  private iocSetIntervalLogout = `${environment.appVersion}-${environment.IOC_SET_INTERVAL_LOGOUT}`;
  private wso2AuthToken = `${environment.appVersion}-${environment.WSO2AUTH_KEY}`;
  private menuActiveKey = `${environment.appVersion}-${environment.MENU_KEY}`;
  private userInfoKey = environment.userInfo;
  private API_URL = `${environment.apiUrl}/authentications`;
  private API_URL_USER = `${environment.apiUrl}/users`;
  //private ssoConfig = environment.SSO_CONFIG;
  private ssoConfig: SSOConfigModel;
  private _httpHeaders: HttpHeaders;
  // public fields
  currentUser$: Observable<UserModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserModel>;
  isLoadingSubject: BehaviorSubject<boolean>;
  private ssoUserInfo: any;
  get currentUserValue(): UserModel {
    return this.currentUserSubject.value;
  }
  set currentUserValue(user: UserModel) {
    this.currentUserSubject.next(user);
  }
  base64: any;
  constructor(
    private _http: HttpClient,
    private authHttpService: AuthHTTPService,
    private _cookie: CookieService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const getSsoConfig = authHttpService.getSsoConfig(environment.SSO_ID).subscribe(
      (payload) => { this.ssoConfig = payload }
    );
    this.subscribe.push(getSsoConfig);
    this._httpHeaders = getHeaderToken(_cookie);
    const subscr = this.getUserByToken().subscribe();
    this.subscribe.push(subscr);
    this.base64 = new Base64();
  }

  getUserByToken(): Observable<UserModel> {
    const accessToken = localStorage.getItem(this.iocAccessToken);
    if (!accessToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    try {
      return this.authHttpService.getUserByToken(accessToken).pipe(
        map((user: UserModel) => {
          if (user) {
            this.currentUserSubject = new BehaviorSubject<UserModel>(user);
            localStorage.setItem(this.userInfoKey, JSON.stringify(user));
          }
          return user;
        }),
        catchError(() => {
          this.removeAuthentication();
          window.location.href = `${window.location.origin}/main`;
          return of(undefined);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
    } catch (err) {
      this.removeAuthentication();
      window.location.href = `${window.location.origin}/main`;
    }
  }
  checkExistUser(userInfo: UserInfo): Observable<any> {
    const url = `${this.API_URL_USER}/get-user-by-email`;
    return this._http.get<any>(url, { headers: this._httpHeaders || null, params: { email: userInfo.email, } }).pipe(
      map((check) => {
        this.ssoUserInfo = userInfo;
        return check;
      }),
      catchError(() => {
        window.location.href = `${window.location.origin}/error/error-3`;
        return of(undefined);
      })
    );
  }

  changePassword(newPassword: string, oldPassword: string, email?: string) {
    const url = `${this.API_URL}/change-password`;
    var body = {
      email: this.currentUserSubject.value ? this.currentUserSubject.value.email : email,
      newPassword: newPassword,
      oldPassword: oldPassword
    };

    return this._http.post(url, body, { headers: this._httpHeaders || null })
      .toPromise()
      .then((res) => {
        const statusCode: StatusCodeData<undefined> = new StatusCodeData<undefined>();
        statusCode.code = 200;
        statusCode.message = MESSAGE_CONST.ChangePassword;
        return statusCode;
      })
      .catch((err) => {
        const statusCode: StatusCodeData<undefined> = new StatusCodeData<undefined>();
        statusCode.code = err.status;
        return checkCodeApi(statusCode, err);
      });
  }

  registerUser(userInfo: UserInfo) {
    const url = `${this.API_URL_USER}`;
    var body = {
      email: userInfo.email,
      fullName: userInfo.name,
      status: true,
    };
    return this._http.post<any>(url, body, { headers: this._httpHeaders || null }).pipe(
      catchError(() => {
        window.location.href = `${window.location.origin}/error/error-3`;
        return of(undefined);
      })
    ).subscribe((reg) => {
      this.signUp(this.ssoUserInfo);
    });
  }

  getAccessToken() {
    if (this._cookie.get(this.wso2AuthToken)) {
      return JSON.parse(this._cookie.get(this.wso2AuthToken)).accessToken;
    }
    return '';

  }

  signUp(userInfo: UserInfo) : Observable<AuthModel> {
    const url = `${this.API_URL}/sign-up`;
    var body = {
      email: userInfo.email,
      userName: userInfo.name,
    };
    return this._http.post<AuthModel>(url, body, { headers: this._httpHeaders || null }).pipe(
      catchError(() => {
        window.location.href = `${window.location.origin}/error/error-3`;
        return of(undefined);
      })
    );
  }

  login(email: string, password: string) {
    const url = `${this.API_URL}/login`;
    var body = {
      email: email,
      password: password,
    };
    return this._http.post(url, body, { headers: this._httpHeaders || null })
      .toPromise()
      .then((res) => {
        const statusCode: StatusCodeData<any> = new StatusCodeData<any>();
        statusCode.code = 200;
        statusCode.message = MESSAGE_CONST.Login;
        statusCode.data = res;
        return statusCode;
      })
      .catch((err) => {
        const statusCode: StatusCodeData<any> = new StatusCodeData<any>();
        statusCode.code = err.status;
        return checkCodeApi(statusCode, err);
      });
  }

  retrieveToken(code) {
    const url = `${this.API_URL}/sign-in-sso?code=${code}&redirectUrl=${this.ssoConfig.redirectUri}`;

    return this._http.get(url, { headers: this._httpHeaders || null }).pipe(
      catchError(() => {
        window.location.href = `${window.location.origin}/error/error-3`;
        return of(undefined);
      })
    ).subscribe(data => this.saveToken(data));
  }

  getExpiresTokenNkg() {
    let expires = localStorage.getItem(this.iocExpiresToken);
    if (expires) return expires;
  }

  getExpiresTokenWso2() {
    let sso = this._cookie.get(this.wso2AuthToken) === '' ? null : JSON.parse(this._cookie.get(this.wso2AuthToken));
    if (sso) return sso.expires;
  }

  refreshToken(isTokenNkg: boolean) {
    let auth = this._cookie.get(this.nkgAuthToken) ?? null;
    let sso = this._cookie.get(this.wso2AuthToken) === '' ? null : JSON.parse(this._cookie.get(this.wso2AuthToken));
    if (isTokenNkg && auth) {
      const refreshToken = localStorage.getItem(this.iocRefreshToken);
      if (refreshToken) {
        const url = `${this.API_URL}/refresh-access-tokens`;
        return this._http.post<AuthModel>(url, { token: refreshToken }).pipe(
          catchError(err => {
            return of(undefined);
          })
        ).subscribe(payload => {
          this._cookie.delete(this.nkgAuthToken);
          localStorage.removeItem(this.iocAccessToken);
          localStorage.removeItem(this.iocRefreshToken);
          localStorage.removeItem(this.iocExpiresToken)
          this.saveTokenNkg(payload);
        });
      }
    }
    else if (!isTokenNkg && sso) {
      const url = `${this.ssoConfig.baseUrl}/${environment.SSO_CONFIG.getTokenUrl}`;
      let httpParams = new HttpParams();
      httpParams = httpParams.append('grant_type', 'refresh_token');
      httpParams = httpParams.append('client_id', this.ssoConfig.clientID);
      httpParams = httpParams.append('client_secret', this.ssoConfig.clientSecret);
      httpParams = httpParams.append('refresh_token', sso.refreshToken);

      let headers =
        new HttpHeaders({ 'Content-type': 'application/x-www-form-urlencoded' });

      return this._http.post(url, httpParams.toString(), { headers: headers }).pipe(
        catchError(err => {
          return of(undefined);
        })
      ).subscribe(payload => {
        this._cookie.delete(this.wso2AuthToken);
        this.saveTokenWso2(payload);
      });
    }

    if (!auth && !sso) {
      this.removeAuthentication();
    }
  }

  saveTokenWso2(token) {
    const now = new Date();
    const expireDate = now.getTime() + (1000 * token.expires_in);
    now.setTime(expireDate);

    let sso = new SsoResponseModel();
    sso.setSso({
      accessToken: token.access_token,
      idToken: token.id_token,
      expires: token.expires_in,
      refreshToken: token.refresh_token
    });
    this._cookie.set(this.wso2AuthToken, JSON.stringify(sso), now, '/');
  }

  saveToken(token) {
    const now = new Date();
    const expireDate = now.getTime() + (1000 * token.expires_in);
    now.setTime(expireDate);

    let sso = new SsoResponseModel();
    sso.setSso({
      accessToken: token.access_token,
      idToken: token.id_token,
      expires: token.expires_in,
      refreshToken: token.refresh_token
    });
    this._cookie.set(this.wso2AuthToken, JSON.stringify(sso), now, '/');
    this.getUserInfo(sso);
  }

  removeAuthentication() {
    localStorage.removeItem(this.menuActiveKey);
    localStorage.removeItem(this.iocAccessToken);
    localStorage.removeItem(this.iocRefreshToken);
    localStorage.removeItem(this.iocExpiresToken);
    localStorage.removeItem(this.iocSetIntervalLogout);
    localStorage.removeItem(this.userInfoKey);
    this._cookie.delete(this.nkgAuthToken);
    this._cookie.delete(this.wso2AuthToken);
  }

  getUserInfo(sso: SsoResponseModel) {
    const url = `${this.API_URL}/get-user-sso?accessToken=${sso.accessToken}`;

    return this._http.get(url, { headers: this._httpHeaders || null }).pipe(
      mergeMap((res: UserInfo) => this.signUp(res))
    ).subscribe((payload: AuthModel) => {
      if (payload) {
        this._cookie.delete(this.nkgAuthToken);
        localStorage.removeItem(this.iocAccessToken);
        localStorage.removeItem(this.iocRefreshToken);
        localStorage.removeItem(this.iocExpiresToken);
        this.saveTokenNkg(payload);
        this.getUserByToken().subscribe();
        window.location.reload();
      }
      else {
        window.location.href = `${window.location.origin}/error/error-3`;
      }
    });
  }

  saveTokenNkg(token: AuthModel) {
    const now = new Date();
    const expireDate = now.getTime() + (1000 * token.expires);
    now.setTime(expireDate);
    localStorage.setItem(this.iocAccessToken, token.accessToken);
    localStorage.setItem(this.iocRefreshToken, token.refreshToken);
    localStorage.setItem(this.iocExpiresToken, token.expires.toString());
    this._cookie.set(this.nkgAuthToken, '719240bf-748e-49fc-b362-4cf0dd46a7a9', now, '/');
  }

  checkCredentials() {
    let checkNkgToken = this._cookie.check(this.nkgAuthToken);
    const isUsingSSOConfig = localStorage.getItem('isUsingSSOConfig');
    if (isUsingSSOConfig === 'true') {
      let checkWso2Token = this._cookie.check(this.wso2AuthToken);
      if (checkNkgToken && checkWso2Token) return true;
    }
    if (checkNkgToken) return true;
    return false;
  }

  getCredentials() {
    return this._cookie.get('access-token');
  }

  revokeNkgToken() {
    const revokeTokenNkgUrl = `${this.API_URL}/access-tokens/revoke`;
    const rvnkg = this._http.post(revokeTokenNkgUrl, null, { headers: this._httpHeaders || null }).subscribe();
    this.subscribe.push(rvnkg);
  }

  revokeWso2Token(sso) {
    let revokeTokenUrl = `${this.ssoConfig.baseUrl}/${environment.SSO_CONFIG.revokeTokenUrl}`;
    let logoutUrl = `${this.ssoConfig.baseUrl}/${environment.SSO_CONFIG.logoutUrl}?id_token_hint=${sso.idToken}&post_logout_redirect_uri=${this.ssoConfig.redirectUri}`;

    let paramsRevoke = new URLSearchParams();
    paramsRevoke.append('client_id', this.ssoConfig.clientID);
    paramsRevoke.append('client_secret', this.ssoConfig.clientSecret);
    paramsRevoke.append('token_type_hint', 'access_token');
    paramsRevoke.append('token', sso.accessToken);
    let headers =
      new HttpHeaders({ 'Content-type': 'application/x-www-form-urlencoded' });
    const rv = this._http.post(revokeTokenUrl, paramsRevoke.toString(), { headers: headers }).subscribe();
    this.subscribe.push(rv);
    return logoutUrl;
  }

  logoutRefresh() {
    var accessToken = localStorage.getItem(this.iocAccessToken);
    if (!accessToken) {
      this.removeAuthentication();
      window.location.href = `${window.location.origin}/`;
    }
  }
  getLoginLocalStorage(): SsoResponseModel {
    let u = this._cookie.get(this.wso2AuthToken);
    if (u) {
      let user = JSON.parse(u) as SsoResponseModel;
      return user;
    }
    return new SsoResponseModel();
  }
  logout() {
    const isUsingSSOConfig = localStorage.getItem('isUsingSSOConfig');
    let sso = this._cookie.get(this.wso2AuthToken) === '' ? null : JSON.parse(this._cookie.get(this.wso2AuthToken));
    let auth = this._cookie.get(this.nkgAuthToken) ?? null;
    if (auth) {
      this.revokeNkgToken();
      this.removeAuthentication();
    }
    if (isUsingSSOConfig === 'true') {
      try {
        localStorage.setItem('isUsingSSOConfig', 'false');
        if (sso) {
          var logoutUrl = this.revokeWso2Token(sso);
          window.location.href = logoutUrl;
        }
        if (!sso && !auth) {
          this.removeAuthentication();
          let loginUrl = `${this.ssoConfig.baseUrl}/${environment.SSO_CONFIG.loginUrl}?response_type=${this.ssoConfig.responseType}&client_id=${this.ssoConfig.clientID}&redirect_uri=${this.ssoConfig.redirectUri}&scope=${this.ssoConfig.scope}`;
          window.location.href = loginUrl;
        }
      } catch (err) {
        window.location.href = `${window.location.origin}/main`;
      }
    } else {
      window.location.href = `/`;
    }
  }
  setRememberMe(flag: boolean) {
    flag ? localStorage.setItem('rememberMe', 'true') : localStorage.removeItem('rememberMe');
  }
  getRememberMe() {
    return localStorage.getItem('rememberMe') ? true : false;
  }
  setLoginId(id: string) {
    id ? localStorage.setItem('ur', this.base64.encode(id)) : localStorage.removeItem('ur');
  }
  getLoginId() {
    return localStorage.getItem('ur') ? this.base64.decode(localStorage.getItem('ur')) : null;
  }
  setLoginPwd(pwd: string) {
    pwd ? localStorage.setItem('pa', this.base64.encode(pwd)) : localStorage.removeItem('pa');
  }
  getLoginPwd() {
    return localStorage.getItem('pa') ? this.base64.decode(localStorage.getItem('pa')) : null;
  }

  timeToLogout(expires: number) {
    const URL_CONFIGURATION = `${environment.apiUrl}/configuration`;
    const SET_INTERVAL_LOGOUT = localStorage.getItem(this.iocSetIntervalLogout);
    if (!SET_INTERVAL_LOGOUT) {
      var refresh = setInterval(() => {
        this._http.get<ConfigurationModel>(URL_CONFIGURATION, { headers: this._httpHeaders })
          .subscribe(config => {
            if ((config.timeExpires * 60) > expires) {
              this.refreshToken(true);
              const isUsingSSOConfig = localStorage.getItem('isUsingSSOConfig');
              if (isUsingSSOConfig === 'true') {
                this.refreshToken(false);
              }
            }
            else {
              this.logout();
            }
          });
      }, (expires - 10) * 1000);
      localStorage.setItem(this.iocSetIntervalLogout, 'true');
    }
  }

  ngOnDestroy(): void {
    this.subscribe.forEach(sb => sb.unsubscribe());
  }

}

